import { useClientPortalAPI } from "@certn/api";
import { useAddressSchema } from "@certn/cascadia";
import {
  AddressFormFormikField,
  CheckboxFormikField,
  Divider,
  Grid,
  GridItem,
  SelectFormikField,
  SelectItem,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { object, string } from "yup";

import {
  initialGroupValues,
  useCreateGroupContext,
} from "./CreateGroupContext";
import { CreateGroupPageLayout } from "./CreateGroupPageLayout";
import { PermissiblePurposeSelect } from "../../../../components/permissible-purpose/PermissiblePurposeSelect";
import { useClientAccount } from "../../../../hooks/useClientAccount";
import { BillingProfileWhatsThis } from "../../../billing-profiles/components/BillingProfileWhatsThis";

export function CreateGroupDetailsStep() {
  const { t } = useTranslation();
  const { client } = useClientAccount();

  const { customerGroup, setCustomerGroup, goToNextStep } =
    useCreateGroupContext();

  const [isAddressSame, setIsAddressSame] = useState(false);
  const addressSchema = useAddressSchema({
    streetAddressFieldName: "address_line_1",
  });
  const schema = object({
    name: string().required().label(t("Group name")),
    applicant_facing_name: string()
      .required()
      .label(t("Applicant facing name")),
    address: addressSchema,
    billing_profile: string().required().label(t("Billing profile")),
    permissible_purpose: string().required().label(t("Permissible purpose")),
  });

  const { data: billingProfiles } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profiles/`,
    { page_size: "all", archived: "ACTIVE" }
  );
  // Check if a company address is valid/exists
  const hasValidCompanyAddress = addressSchema.isValidSync(client?.address);
  return (
    <Formik
      initialValues={customerGroup}
      onSubmit={(values) => {
        setCustomerGroup(values);
        goToNextStep();
      }}
      validationSchema={schema}
    >
      {({ isSubmitting, values, setFieldValue }) => {
        return (
          <Form noValidate>
            <CreateGroupPageLayout
              title={t("Create New Group")}
              submitting={isSubmitting}
            >
              <Grid form testID="create-customer-group-grid">
                <Text style="form/section-heading">{t("Group details")}</Text>
                <TextInputFormikField
                  testID="group-name"
                  name="name"
                  label={t("Group name")}
                  isRequired
                />
                <TextInputFormikField
                  testID="applicant-facing-name"
                  name="applicant_facing_name"
                  label={t("Applicant facing name")}
                  isRequired
                />
                <SelectFormikField
                  name="billing_profile"
                  label={t("Billing profile")}
                  testID="billing-profile-select"
                  isRequired
                  info={<BillingProfileWhatsThis />}
                >
                  {billingProfiles?.results.map((profile) => (
                    <SelectItem
                      key={profile.id}
                      id={profile.id}
                      textValue={
                        profile.payer_option === "APPLICANT"
                          ? `${profile.profile_name ?? ""} (${t("Applicant Pay")})`
                          : profile.profile_name ?? undefined
                      }
                    >
                      {profile.profile_name}{" "}
                      {profile.payer_option === "APPLICANT" &&
                        `(${t("Applicant Pay")})`}
                    </SelectItem>
                  ))}
                </SelectFormikField>
                <PermissiblePurposeSelect
                  name="permissible_purpose"
                  label={t("Permissible purpose")}
                  testID="permissible-purpose-select"
                  isRequired
                  showInfo
                />
                <Divider />
                <Text style="form/section-heading">{t("Group address")}</Text>
                {hasValidCompanyAddress && (
                  <GridItem>
                    <CheckboxFormikField
                      testID="address-same-as-company"
                      name="address_same_as_company"
                      onChange={(checked) => {
                        setIsAddressSame(checked);
                        setFieldValue(
                          "address",
                          checked ? client?.address : initialGroupValues.address
                        );
                      }}
                    >
                      <Trans>Same as company</Trans>
                      {isAddressSame && (
                        <div className={styles.addressWrapper}>
                          <Text style="input/label">
                            {values.address?.address_line_1}
                            <br />
                            {values.address?.locality || "--"},{" "}
                            {values.address?.administrative_division_1 || "--"}{" "}
                            {values.address?.country || "--"}
                            <br />
                            {values.address?.postal_code || "--"}
                          </Text>
                        </div>
                      )}
                    </CheckboxFormikField>
                  </GridItem>
                )}
                {!isAddressSame && <AddressFormFormikField name="address" />}
              </Grid>
            </CreateGroupPageLayout>
          </Form>
        );
      }}
    </Formik>
  );
}

const styles = {
  addressWrapper: css({
    marginTop: "var(--space-2)",
  }),
};
